import type { InputHTMLAttributes, Ref } from 'react';

import Icon, { type IconName } from 'components/Icon';
import { getInputClassName, isLabelFloating } from 'components/InputUi';
import PlainButton from 'components/PlainButton';
import { useRefObject } from 'hooks';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import FloatingLabel from './FloatingLabel';
import InlineLabel from './InlineLabel';
import InputInfo from './InputInfo';

// React has HTMLInputTypeAttribute but several of those (like 'button' and
// 'checkbox') are not relevant here since this is specifically a text field.
export type InputType =
	| 'date'
	| 'datetime-local'
	| 'email'
	| 'month'
	| 'number'
	| 'password'
	| 'search'
	| 'tel'
	| 'text'
	| 'time'
	| 'url'
	| 'week';

export interface Props extends InputHTMLAttributes<HTMLInputElement> {
	type?: InputType;
	errorMessage?: string | string[];
	helpText?: string;
	hiddenLabel?: boolean;
	icon?: IconName;
	id: string;
	inputClassName?: string;
	invalid?: boolean;
	label: string;
	labelClassName?: string;
	labelType?: 'floating' | 'inline';
	onClearClick?: () => void;
	ref?: Ref<HTMLInputElement>;
	valid?: boolean;
}

export default function Input({
	className,
	defaultValue,
	disabled,
	errorMessage,
	helpText,
	hiddenLabel = false,
	icon,
	id,
	inputClassName,
	inputMode,
	invalid = false,
	label,
	labelClassName,
	labelType = 'floating',
	onClearClick,
	readOnly,
	ref,
	required,
	type = 'text',
	valid = false,
	value,
	...attrs
}: Props) {
	const { t } = useI18n();
	const inputRef = useRefObject(ref);

	const errorMessageId = `${id}-error`;
	const helpTextId = `${id}-help`;

	let inputType = type;
	let mode = inputMode;
	let pattern: string | undefined;
	if (type === 'number') {
		inputType = 'text';
		pattern = String.raw`\d*`;
		mode = 'numeric';
	}

	// Stringify to make zero truthy.
	const hasClearButton = Boolean(onClearClick && value?.toString());
	const isEditable = !disabled && !readOnly;

	return (
		<div className={className}>
			{labelType === 'inline' && (
				<InlineLabel
					className={cn('pb-1', labelClassName)}
					disabled={!isEditable}
					htmlFor={id}
					invalid={invalid}
					isHidden={hiddenLabel}
					required={required}
					valid={valid}
				>
					{label}
				</InlineLabel>
			)}
			<div className="relative">
				<input
					{...attrs}
					ref={inputRef}
					className={getInputClassName({
						disabled: !isEditable,
						extra: cn(
							'h-14 py-0',
							// Match right padding with clear button width further down.
							(icon || hasClearButton) && 'pr-12',
							inputClassName,
						),
						invalid,
						valid,
					})}
					aria-describedby={[errorMessageId, helpTextId].join(' ')}
					// Support for aria-errormessage isn't good enough as of autumn 2024
					// so stick with aria-describedby for now.
					// aria-errormessage={errorMessageId}
					aria-invalid={invalid}
					defaultValue={defaultValue}
					disabled={disabled}
					id={id}
					inputMode={mode}
					pattern={pattern}
					readOnly={readOnly}
					required={required}
					type={inputType}
					value={value}
				/>
				{labelType === 'floating' && (
					<FloatingLabel
						className={labelClassName}
						disabled={!isEditable}
						htmlFor={id}
						invalid={invalid}
						isFloating={
							isLabelFloating(value, defaultValue) ||
							// Date and time inputs have placeholder-like content to show
							// formats, but they aren't placeholders so the placeholder
							// detection styles in the label don't trigger. Always float
							// to get around it.
							is.oneOf(type, 'date', 'datetime-local', 'month', 'time', 'week')
						}
						isHidden={hiddenLabel}
						required={required}
						valid={valid}
					>
						{label}
					</FloatingLabel>
				)}
				{icon && !hasClearButton && (
					<Icon
						name={icon}
						className="absolute top-1/2 right-3 -translate-y-1/2"
					/>
				)}
				{hasClearButton && isEditable && (
					<PlainButton
						className={cn(
							// Match width with right padding on the input further up.
							'w-12',
							'absolute top-1/2 right-0 flex h-14 -translate-y-1/2 items-center justify-center border-none bg-none hover:opacity-80',
						)}
						onClick={() => {
							inputRef.current?.focus();
							onClearClick?.();
						}}
					>
						<span className="sr-only">{t('general_clear_button_label')}</span>
						<Icon name="circle-cancel" color="base" />
					</PlainButton>
				)}
			</div>
			<InputInfo
				errorMessage={errorMessage}
				errorMessageId={errorMessageId}
				helpText={helpText}
				helpTextId={helpTextId}
				invalid={invalid}
			/>
		</div>
	);
}
Input.displayName = 'FormUI_Input';
